import React, { Suspense, lazy } from 'react'
import * as ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'

import { OAuth } from '@kistler/skybase-oauth/oauth.js'
import { OAuthWrapper } from '@/oauth-wrapper'
import { setAppRoot } from '@kistler/skybase-ui/skybase-core/base/actions'
import { getAppRoot } from '@kistler/skybase-ui/skybase-core/utils/browser'
import { notificationsLocalStorageService } from '@kistler/skybase-ui/skybase-core/notifications/notifications-localstorage-service'
import { emitterLogger } from '@kistler/skybase-ui/skybase-core/emitter/emitter-logger'
import { emitToastOnErrorService } from '@kistler/skybase-ui/skybase-components/sb-toastr/services'
import { combineLocations } from '@kistler/skybase-ui/skybase-core/notifications/combine-locations'
import { SbErrorBoundary } from '@kistler/skybase-pages/sb-error-boundary/sb-error-boundary.js'

import CustomIntlProvider from './containers/custom-intl-provider/custom-intl-provider.jsx'
import { initializeLanguagesAndCulture } from './common/locales/utils.js'
import { getMode } from './utils/get-mode.js'
import { SbLoader } from '@kistler/skybase-ui/skybase-components/sb-loader/sb-loader'
import { loaderSize } from '@kistler/skybase-ui/skybase-components/sb-loader/constants'
import store from './stores/store.js'
import './styles/initial-loaders.scss' // preload fonts to correctly display loaders ASAP
import '@kistler/skybase-pages/sb-error-boundary/sb-error-page.scss'

const { REACT_APP_OAUTH_CONFIG_PATH } = process.env

const root = ReactDOM.createRoot(document.getElementById('app'))
root.render(<SbLoader show size={loaderSize.L} />)

fetch(REACT_APP_OAUTH_CONFIG_PATH || '/oAuthConfig.json', { headers: { 'Cache-Control': 'no-cache, must-revalidate' } })
  .then(
    response => response.json(),
    err => {
      console.error('Could not fetch config file (1): ', err)
      // try again
      return fetch(REACT_APP_OAUTH_CONFIG_PATH || '/oAuthConfig.json', {
        headers: { 'Cache-Control': 'no-cache, must-revalidate' },
      }).then(
        response => response.json(),
        err => {
          console.error('Could not fetch config file (2): ', err)
          root.render(<h1>An error occurred. Try again later</h1>)
          return null
        },
      )
    },
  )
  .then(
    config => {
      if (!config) {
        return
      }
      // initialize skybase-oauth services
      OAuth.setConfig(config)

      store.dispatch(setAppRoot(getAppRoot()))

      // initialize the storage for the notification service
      notificationsLocalStorageService(store)

      // initialize locations for redirect for notifications
      combineLocations({}) // Add custom locations to parameters here

      // initialize toast to error message service.
      emitToastOnErrorService(store.dispatch)

      if (getMode() !== 'production') {
        emitterLogger()
      }

      // Sets up languages and cultures.
      initializeLanguagesAndCulture(store.dispatch)

      const ProtectedContent = lazy(
        () => import('./oauth-protected-content.jsx'),
      )
      const MainComponent = () => {
        return (
          <Provider store={store}>
            <CustomIntlProvider>
              <SbErrorBoundary>
                <OAuthWrapper renderFactory={!OAuth.isInLocalSDKMode}>
                  <Suspense fallback={<SbLoader show size={loaderSize.L} />}>
                    <ProtectedContent />
                  </Suspense>
                </OAuthWrapper>
              </SbErrorBoundary>
            </CustomIntlProvider>
          </Provider>
        )
      }

      root.render(<MainComponent />)
    },
    err => console.error('Could not parse config file: ', err),
  )
